import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useGetAuthApi, usePostAuthApi, useDeleteAuthApi } from '@/hooks/UseApiCalls';
import { queryClient } from '@/hooks/QueryClient';
import MainDialogLayout from '@/components/ui/MainDialogLayout/MainDialogLayout';
import ConfirmationDialog from '@/components/ui/ConfirmationDialog/ConfirmationDialog';
import { SupportedAdditionalInfoDtoIn } from '@/models/in/SupportedAdditionalInfoDtoIn';
import { BankAccountDtoIn } from '@/models/in/BankAccountDtoIn';
import { PaymentInfoUsageDtoIn } from '@/models/in/PaymentInfoUsageDtoIn';
import { PaymentMethodType } from '@/models/common/PaymentMethodType';
import { BankAccountDetailsDtoOut } from '@/models/out/BankAccountDetailsDtoOut';
import ExistingPaymentInfos from './ExistingPaymentInfos';
import AddPaymentInfoForm, { PaymentInfoFormData, createEmptyPaymentInfo } from './AddPaymentInfoForm';
import styles from './AddPaymentInfoPopup.module.scss';

interface AddPaymentInfoPopupProps {
  fundId: number;
  supportedId: number;
  open: boolean;
  setOpen: (open: boolean) => void;
  onRefreshList?: () => Promise<any>;
}

const AddPaymentInfoPopup = ({ fundId, supportedId, open, setOpen, onRefreshList }: AddPaymentInfoPopupProps) => {
  const { t } = useTranslation();
  const [newPaymentInfos, setNewPaymentInfos] = useState<PaymentInfoFormData[]>([]);
  const [accountToDelete, setAccountToDelete] = useState<BankAccountDtoIn | null>(null);

  const additionalInfoApiUrl = fundId && supportedId ? `funds/${fundId}/supporteds/${supportedId}/additional-info` : '';
  const { data: additionalInfo } = useGetAuthApi<SupportedAdditionalInfoDtoIn>(additionalInfoApiUrl);

  const addPaymentInfoApiUrl = fundId && supportedId ? `funds/${fundId}/supporteds/${supportedId}/payment-infos` : '';
  const { mutateAsync: addPaymentInfos, isPending: isSubmitting } = usePostAuthApi<any, BankAccountDetailsDtoOut[]>(addPaymentInfoApiUrl);

  const paymentInfoApiUrl = accountToDelete ? `funds/${fundId}/supporteds/${supportedId}/payment-infos/${accountToDelete.id}` : '';
  // כמה בקשות תמיכה עוד תלויות בחשבון, ואם קרן אחרת משתמשת בו - נשאל לפני שמציגים אישור,
  // כדי שהרכז יראה את המשמעות ולא יגלה אותה מהשגיאה שחוזרת מהשרת.
  const { data: usage, isError: isUsageError } = useGetAuthApi<PaymentInfoUsageDtoIn>(paymentInfoApiUrl ? `${paymentInfoApiUrl}/usage` : '');
  const { mutate: deletePaymentInfo, isPending: isDeleting } = useDeleteAuthApi<void>(paymentInfoApiUrl);

  // Reset state when popup closes, and add one empty payment info when opening
  useEffect(() => {
    if (open) {
      // When opening, start with one empty payment info
      if (newPaymentInfos.length === 0) {
        setNewPaymentInfos([createEmptyPaymentInfo()]);
      }
    } else {
      // When closing, reset everything
      setNewPaymentInfos([]);
      setAccountToDelete(null);
    }
  }, [open]);

  const isFormValid =
    newPaymentInfos.length > 0 && newPaymentInfos.every((info) => info.accountOwnerName && info.bankNumber && info.branchNumber && info.accountNumber);

  const refreshSupportedData = async () => {
    // Invalidate additional-info queries to refresh data in AdditionalInfo blade
    queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey[0] as string;
        return key?.includes(`supporteds/${supportedId}/additional-info`);
      },
    });

    // Refresh supporteds list
    await onRefreshList?.();
  };

  const handleSubmit = async () => {
    if (!isFormValid) return;

    try {
      const paymentInfosToSubmit: BankAccountDetailsDtoOut[] = newPaymentInfos.map((info) => ({
        type: PaymentMethodType.BankAccount,
        bankNumber: info.bankNumber,
        branchNumber: info.branchNumber,
        accountNumber: info.accountNumber,
        accountOwnerName: info.accountOwnerName,
        documents: [],
      }));

      // Call API endpoint
      await addPaymentInfos(paymentInfosToSubmit);

      // Show success message
      alert(t('paymentInfoAddedSuccessfully'));

      // Close popup immediately
      setOpen(false);

      await refreshSupportedData();
    } catch (error: any) {
      if (error?.response?.status === 409) {
        alert(t('duplicatePaymentInfoError'));
      } else {
        alert(error?.message || t('errorAddingPaymentInfo'));
      }
    }
  };

  // עד שהשרת עונה אין לדעת אם מותר למחוק, ולכן הדיאלוג מחכה ולא מציע מחיקה שתידחה.
  // גם כשל בבדיקה נחשב חסימה — לא מוחקים חשבון בנק כשלא ידוע מי עוד משלם אליו.
  const isUsageResolved = usage !== undefined || isUsageError;
  const isDeleteBlocked = isUsageResolved && usage?.canDelete !== true;

  const handleConfirmDelete = () => {
    if (!accountToDelete) return;

    // כשהמחיקה חסומה, הדיאלוג הוא הודעה בלבד והכפתור רק סוגר אותו.
    if (isDeleteBlocked) {
      setAccountToDelete(null);
      return;
    }

    deletePaymentInfo(undefined, {
      onSuccess: async () => {
        setAccountToDelete(null);
        await refreshSupportedData();
      },
      onError: (error) => {
        setAccountToDelete(null);
        // 409 מגיע רק כשמצב הבקשות השתנה בין הבדיקה לאישור — השרת הוא שמחזיק את המדיניות.
        if (axios.isAxiosError(error) && error.response?.status === 409) {
          alert(t('deleteBankAccountBlocked'));
        } else {
          alert(t('errorDeletingBankAccount'));
        }
      },
    });
  };

  const deleteConfirmMessage = () => {
    if (!accountToDelete) return undefined;
    if (isUsageError) return t('errorCheckingBankAccountUsage');
    if (!usage) return undefined;

    const account = `${t('bank')} ${accountToDelete.bankNumber} (${accountToDelete.bankName}), ${t('branch')} ${accountToDelete.branchNumber}, ${t('account')} ${accountToDelete.accountNumber}`;

    if (usage.activeRequestsInFund > 0) {
      return t('deleteBankAccountBlockedByLinkedRequests', { account, count: usage.activeRequestsInFund });
    }

    if (usage.usedByOtherFunds) {
      return t('deleteBankAccountBlockedByOtherFund');
    }

    return t('deleteBankAccountConfirm', { account });
  };

  const isSubmitDisabled = !isFormValid || isSubmitting;

  return (
    <MainDialogLayout open={open} onOpenChange={setOpen} title={t('paymentDetails')}>
      <div className={styles.wrapper}>
        <div className={styles.content}>
          {/* Existing Payment Infos Section */}
          <ExistingPaymentInfos
            paymentInfos={additionalInfo?.paymentInfos || []}
            onDelete={setAccountToDelete}
            deletingId={isDeleting ? accountToDelete?.id : null}
          />

          {/* Divider */}
          {additionalInfo?.paymentInfos && additionalInfo.paymentInfos.length > 0 && <div className={styles.divider} />}

          {/* Add New Payment Info Section */}
          <AddPaymentInfoForm paymentInfos={newPaymentInfos} onPaymentInfosChange={setNewPaymentInfos} />
        </div>

        {/* Footer with Submit Button */}
        <div className={styles.footer}>
          <button onClick={handleSubmit} disabled={isSubmitDisabled} className={isSubmitDisabled ? styles.submitButtonDisabled : styles.submitButton}>
            {isSubmitting ? t('saving') : t('savePaymentInfo')}
          </button>
        </div>
      </div>

      <ConfirmationDialog
        open={accountToDelete !== null}
        onOpenChange={(isOpen) => {
          if (!isOpen) setAccountToDelete(null);
        }}
        title={t('deleteBankAccountTitle')}
        message={deleteConfirmMessage()}
        confirmText={isDeleteBlocked ? 'close' : 'confirmDeleteBankAccount'}
        cancelText="cancel"
        onConfirm={handleConfirmDelete}
        onCancel={() => setAccountToDelete(null)}
        isLoading={isDeleting || !isUsageResolved}
        variant={isDeleteBlocked ? 'default' : 'danger'}
        withIcon={isDeleteBlocked}
        hideCancel={isDeleteBlocked}
      />
    </MainDialogLayout>
  );
};

export default AddPaymentInfoPopup;
